/**
 * Fiscal year service - delegates to API (single source of truth).
 * All business logic lives in the backend FiscalYearHelper.
 */
import { parseISO } from 'date-fns';
import { apiService } from './api';
import { formatCalendarDateIso } from '../utils/dateFormat';
import { API_ENDPOINTS } from '../constants/api';

export interface FiscalYearCurrent {
  fiscalYear: number;
  quarter: number;
  quarterStart: string;
  quarterEnd: string;
}

export interface FiscalYearQuarterRange {
  start: string;
  end: string;
}

export interface FiscalYearEvaluate {
  fiscalYear: number;
  quarter: number;
}

/** Year and quarter using the same alignment as IDP and review cycles (UseCalendarYear). */
export interface ReviewYearEvaluate {
  year: number;
  quarter: number;
}

export const fiscalYearService = {
  /** Get current fiscal year and quarter from API. */
  async getCurrent(): Promise<FiscalYearCurrent> {
    const res = await apiService.get<FiscalYearCurrent>(API_ENDPOINTS.SYSTEM.FISCAL_YEAR_CURRENT);
    return res.data;
  },

  /** Get quarter date range for a fiscal year and quarter from API. */
  async getQuarterRange(fiscalYear: number, quarter: number): Promise<FiscalYearQuarterRange> {
    const res = await apiService.get<FiscalYearQuarterRange>(
      API_ENDPOINTS.SYSTEM.FISCAL_YEAR_QUARTER_RANGE(fiscalYear, quarter)
    );
    return res.data;
  },

  /** Evaluate a date to get its fiscal year and quarter from API. */
  async evaluateDate(date: Date | string): Promise<FiscalYearEvaluate> {
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const res = await apiService.get<FiscalYearEvaluate>(
      API_ENDPOINTS.SYSTEM.FISCAL_YEAR_EVALUATE(dateStr)
    );
    return res.data;
  },

  /** Evaluate a date using the same alignment as IDP and review cycles (UseCalendarYear). Use when matching cycles to IDP year. */
  async evaluateReviewYearDate(date: Date | string): Promise<ReviewYearEvaluate> {
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const res = await apiService.get<ReviewYearEvaluate>(
      API_ENDPOINTS.SYSTEM.REVIEW_YEAR_EVALUATE(dateStr)
    );
    return res.data;
  },

  /** Full fiscal year bounds (Q1 start through Q4 end), same calendar as API quarter ranges. */
  async getFiscalYearDateRange(fiscalYear: number): Promise<{ from: string; to: string }> {
    const [q1, q4] = await Promise.all([
      this.getQuarterRange(fiscalYear, 1),
      this.getQuarterRange(fiscalYear, 4),
    ]);
    const from = formatCalendarDateIso(parseISO(q1.start));
    const to = formatCalendarDateIso(parseISO(q4.end));
    return { from, to };
  },
};
